import React from 'react';
import _ from 'lodash';

import Avatar from '@mui/material/Avatar';

import { AudienceCard } from '../modules/audience-card';
import { CategoryDashboard } from '../modules/audience-category-dashboard';
import RetargetingModal from './modules';
import flags from 'containers/flags/service';

export const retargetingOptionsMapping = {
    impression: {
        label: 'Impressions',
        color: '#FFCE50',
    },
    click: {
        label: 'Clicks',
        color: '#FFB0C1',
    },
    conversion: {
        label: 'Creative engagements',
        color: '#6ADF7E',
    },
    attribution: {
        label: 'Campaign-driven conversions',
        color: '#9FDCFD',
    },
    pixel: {
        label: 'All site visits or conversions',
        color: '#B1B1C7',
    },
};

const subCategoryColor = {
    Impressions: '#FFCE50',
    Clicks: '#FFB0C1',
    'Creative engagements': '#6ADF7E',
    attribution: '#9FDCFD',
    'All site visits or conversions': '#B1B1C7',
};

const tabValueMapping = {
    all: {
        value: ['impression', 'click', 'conversion', 'attribution', 'pixel'],
        label: 'All',
    },
    impression: {
        value: ['impression'],
        label: 'Impressions',
    },
    click: {
        value: ['click'],
        label: 'Clicks',
    },
    conversion: {
        value: ['conversion'],
        label: 'Creative engagements',
    },
    attribution: {
        value: ['attribution'],
        label: 'Campaign-driven conversions',
    },
    pixel: {
        value: ['pixel'],
        label: 'All site visits or conversions',
    },
};

const tabValueMappingV2 = {
    all: {
        value: [
            'Impressions',
            'Clicks',
            'Creative engagements',
            'Campaign-driven conversions',
            'All site visits or conversions',
        ],
        label: 'All',
    },
    impression: {
        value: ['Impressions'],
        label: 'Impressions',
    },
    click: {
        value: ['Clicks'],
        label: 'Clicks',
    },
    conversion: {
        value: ['Creative engagements'],
        label: 'Creative engagements',
    },
    attribution: {
        value: ['Campaign-driven conversions'],
        label: 'Campaign-driven conversions',
    },
    pixel: {
        value: ['All site visits or conversions'],
        label: 'All site visits or conversions',
    },
};

const getTabs = ({ audiences }) => {
    const tabs = [
        {
            value: 'all',
            label: 'All',
            avatar: <Avatar>{audiences && audiences.length}</Avatar>,
        },
        {
            value: 'impression',
            label: 'Impressions',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Impressions';
                            } else {
                                return audience.retargeting_type === 'impression';
                            }
                        }).length}
                </Avatar>
            ),
        },
        {
            value: 'click',
            label: 'Clicks',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Clicks';
                            } else {
                                return audience.retargeting_type === 'Clicks';
                            }
                        }).length}
                </Avatar>
            ),
        },
        {
            value: 'conversion',
            label: 'Creative engagements',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Creative engagements';
                            } else {
                                return audience.retargeting_type === 'conversion';
                            }
                        }).length}
                </Avatar>
            ),
        },
        {
            value: 'attribution',
            label: 'Campaign-driven conversions',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Campaign-driven conversions';
                            } else {
                                return audience.retargeting_type === 'attribution';
                            }
                        }).length}
                </Avatar>
            ),
        },
        {
            value: 'pixel',
            label: 'Pixels',
            avatar: (
                <Avatar>
                    {audiences &&
                        _.filter(audiences, audience => {
                            if (
                                flags.isEnabled(
                                    'en_2987_use_audience_category_and_subCategory_code'
                                )
                            ) {
                                return audience.subCategory === 'Pixels';
                            } else {
                                return audience.retargeting_type === 'pixel';
                            }
                        }).length}
                </Avatar>
            ),
        },
    ];

    return tabs;
};

const sortingOptions = [
    {
        label: 'Sort by: Newest',
        value: '_created',
        direction: 'desc',
    },
    {
        label: 'Sort by: Last Updated',
        value: 'lastRefreshed',
        direction: 'desc',
    },
    {
        label: 'Sort by: Name',
        value: 'name',
        direction: 'asc',
    },
];

const RetargetingLayout = ({
    audienceSegments,
    onSearch,
    openModal,
    download,
    isSegmentDownloading,
    totalSegmentCount,
    activeAudience,
    organizationId,
}) => {
    const tabs = getTabs({ audiences: audienceSegments });

    const linkToAudienceTab = {
        pathname: '/audiences/all',
        query: { audienceTab: 'custom_audiences' },
        linkBackLabel: 'Your Custom Audiences',
    };

    return (
        <CategoryDashboard
            audienceSegments={audienceSegments}
            onSearch={onSearch}
            segmentCount={totalSegmentCount}
            createEditAction={openModal}
            activeAudience={activeAudience}
            organizationId={organizationId}
            sortingOptions={sortingOptions}
            description={
                'Built to reach audiences defined by project-based insights, this segment utilizes data beyond standard attributes to meet strategic objectives.'
            }
            title={'Retargeting'}
            linkToAudienceTab={linkToAudienceTab}
            bannerImagePath={'/images/audience-segments/banners/retargeting-banner.png'}
            modal={<RetargetingModal />}
            tabFields={{
                tabs,
                tabValueMapping,
                tabValueMappingV2,
                valueField: 'retargeting_type',
            }}
            allowExternalCreate={true}
            renderAudienceCard={segment => (
                <AudienceCard
                    segment={segment}
                    onClick={() => openModal(segment.id)}
                    categoryLabel={
                        flags.isEnabled('en_2987_use_audience_category_and_subCategory_code')
                            ? segment.subCategory
                            : retargetingOptionsMapping[segment.retargeting_type].label
                    }
                    categoryColor={
                        flags.isEnabled('en_2987_use_audience_category_and_subCategory_code')
                            ? subCategoryColor[segment.subCategory]
                            : retargetingOptionsMapping[segment.retargeting_type].color
                    }
                    onDownload={download}
                    isSegmentDownloading={isSegmentDownloading(segment)}
                    active={segment.id === activeAudience}
                    organizationId={organizationId}
                />
            )}
        />
    );
};

export default RetargetingLayout;
